import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule, Table } from 'primeng/table';
import { SortEvent as PrimeSortEvent } from 'primeng/api';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { Candidate } from '../../../models/candidate-model';
import { CompanyDisplayComponent } from '../../company-display/company-display.component';
import { WsyCurrencyPipe } from '../../../pipes/wsy-currency.pipe';
import { WsyPercentagePipe } from '../../../pipes/wsy-percentage.pipe';
import { StockTableSkeletonComponent } from './skeleton/stock-table-skeleton.component';
import { StockAccordionSkeletonComponent } from '../../lists/stock-accordion-list/skeleton/stock-accordion-skeleton.component';
import { ScoreBadgeComponent } from '../../badges/score-badge.component';
import { PremiumBadgeComponent } from '../../badges/premium-badge.component';
import { WatchlistAddIconButtonComponent } from '../../watchlists/watchlist-add-icon-button/watchlist-add-icon-button.component';
import { StockAccordionListComponent } from '../../lists/stock-accordion-list/stock-accordion-list.component';

export interface TableSortEvent {
  field: string;
  order: number; // 1 for ascending, -1 for descending
}

@Component({
  selector: 'app-stock-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    CompanyDisplayComponent,
    WsyCurrencyPipe,
    WsyPercentagePipe,
    StockTableSkeletonComponent,
    StockAccordionSkeletonComponent,
    ScoreBadgeComponent,
    PremiumBadgeComponent,
    WatchlistAddIconButtonComponent,
    StockAccordionListComponent
  ],
  templateUrl: './stock-table.component.html',
  styleUrls: ['./stock-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockTableComponent {
  // ViewChild for p-table reference
  table = viewChild<Table>('stockTable');

  // Input properties
  stocks = input<Candidate[]>([]);
  isLoading = input<boolean>(false);
  sortField = input<string | undefined>(undefined);
  sortOrder = input<number | undefined>(undefined);

  // Output events
  stockSelected = output<Candidate>();
  sortChanged = output<TableSortEvent>();

  // Breakpoint observer for responsive design
  private breakpointObserver = inject(BreakpointObserver);
  isMobileView = toSignal(
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(
      map(result => result.matches)
    ),
    { initialValue: false }
  );

  // Computed signals for derived state
  hasStocks = computed(() => this.stocks().length > 0);

  constructor() {
    // Apply initial sort state when inputs change or when data changes
    effect(() => {
      const field = this.sortField();
      const order = this.sortOrder();
      const tableRef = this.table();

      // Exit early if no sort state is provided
      if (!field || order === undefined) {
        if (tableRef) {
          tableRef.sortField = undefined;
          tableRef.sortOrder = 1;
        }
        return;
      }

      const stocksData = this.stocks();

      if (tableRef && stocksData.length > 0) {
        // Apply sort programmatically using PrimeNG's sortSingle method
        tableRef.sortField = field;
        tableRef.sortOrder = order;
        tableRef.sortSingle();
      }
    });
  }

  // TrackBy function for performance
  trackByTicker(index: number, item: Candidate): string {
    return item.ticker;
  }

  // Handle stock row click
  onStockClick(stock: Candidate | Candidate[] | undefined): void {
    if (stock && !Array.isArray(stock)) {
      this.stockSelected.emit(stock);
    }
  }

  stockClicked(ticker: string) {
    const stock = this.stocks().find(candidate => candidate.ticker === ticker);

    if (stock) {
      this.onStockClick(stock);
      return;
    }
  }

  // Handle sort event from PrimeNG table
  onSort(event: PrimeSortEvent): void {
    if (event.field && event.order !== null && event.order !== undefined) {
      this.sortChanged.emit({
        field: event.field,
        order: event.order
      });
    }
  }
}
